import { ProductLabel } from "../../ui/product-label.tsx";
import { ChannelName } from "../../ui/channel.tsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { WarningCircleIcon } from "@phosphor-icons/react";
import type * as C from "@zooi/contracts";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { number } from "../../format.ts";
import { INBOX_LABELS as I, POLICY_KIND } from "../../labels.ts";
import { Button, Callout, ErrorText, Kbd, Pill } from "../../ui.tsx";
import { More, PointList } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { Chip } from "../../ui/status.tsx";
import { useInbox, Review, DetailPanelShell } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, the reply queue): one reply
// open at a time. The person opens it on X with their words filled in, sends
// it there, and marks it here; zooi never posts.

/** X's own reply box with the draft filled in; the person presses send there. */
const replyLink = (postId: string, body: string) => `https://x.com/intent/tweet?${new URLSearchParams({ in_reply_to: postId, text: body })}`;

/** How long X counts a text: most scripts weigh 2, Latin 1, and a link 23 however long it is. */
export function xLength(text: string) {
  const links = text.match(/https?:\/\/\S+/g) ?? [];
  const rest = links.reduce((left, link) => left.replace(link, ""), text);
  let weight = 23 * links.length;
  for (const char of rest) {
    const code = char.codePointAt(0)!;
    weight += code <= 4351 || (code >= 8192 && code <= 8205) || (code >= 8208 && code <= 8223) || (code >= 8242 && code <= 8247) ? 1 : 2;
  }
  return weight;
}
const X_LIMIT = 280;

export function ReplyQueueReview() {
  const { replyQueueId } = useParams({ strict: false }) as { replyQueueId: string };
  const { items, intent, done } = useInbox();
  const client = useQueryClient();
  const queue = useQuery(q.replyQueue(replyQueueId));
  const item = items.find((entry) => entry.kind === "reply_queue" && entry.id === replyQueueId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const primary = useRef<HTMLButtonElement>(null);
  const editor = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { setError(undefined); setEdits({}); }, [replyQueueId]);
  useEffect(() => { if (queue.data && intent === "approve") primary.current?.focus(); }, [intent, queue.data, replyQueueId]);
  // The next reply opens once the last one is decided, and the keyboard goes with it.
  const activeId = queue.data?.items.find((reply) => reply.status === "queued")?.id;
  const moving = useRef(false);
  useEffect(() => { if (moving.current && activeId && !busy) { moving.current = false; primary.current?.focus(); } }, [activeId, busy]);

  if (queue.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!queue.data) return <DetailPanelShell><ErrorText error={queue.error} /></DetailPanelShell>;
  const data = queue.data;
  const open = data.status === "approved" && data.action_status === "committed";
  const queued = data.items.filter((reply) => reply.status === "queued");
  const active = open ? queued[0] : undefined;
  const text = active ? edits[active.id] ?? active.body : "";
  const length = xLength(text);
  const usable = text.trim().length >= 2;
  const link = active && usable ? replyLink(active.target_post_id, text) : null;

  const decide = async (status: "posted" | "skipped") => {
    if (!active || busy) return;
    setBusy(true); setError(undefined);
    try {
      const next = await api.post<C.ReplyQueue>(`/api/reply-items/${encodeURIComponent(active.id)}/decide`, status === "posted" && text.trim() !== active.body ? { status, body: text.trim() } : { status });
      client.setQueryData(q.replyQueue(replyQueueId).queryKey, next);
      if (!next.items.some((reply) => reply.status === "queued")) done(I.repliesDone);
      else moving.current = true;
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  // Single keys act only inside this panel, and never while typing the reply.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === editor.current) {
      if (event.key === "Escape") { event.stopPropagation(); event.preventDefault(); primary.current?.focus(); }
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey || !active || (event.target as HTMLElement).tagName === "INPUT") return;
    const key = event.key.toLowerCase();
    if (key === "a" && usable) void decide("posted");
    else if (key === "s") void decide("skipped");
    else if (key === "o" && link) window.open(link, "_blank", "noopener,noreferrer");
    else if (key === "e") editor.current?.focus();
    else return;
    event.preventDefault();
  };

  return <Review label={I.detail} title={data.title}
    meta={<><Pill tone={queued.length ? "teal" : "gray"} status>{queued.length ? I.repliesLeft(queued.length) : I.repliesDone}</Pill><ChannelName channel={data.channel} />{item && <ProductLabel product={item.product} />}</>}>
    <div className="z-flow" onKeyDown={onKeyDown}>
      <Callout>{I.repliesNote}</Callout>
      {!open && <p className="z-small z-muted">{data.status === "expired" ? I.repliesExpired : I.repliesDone}</p>}
      {open && data.expires_at && <p className="z-small z-muted">{I.expires(data.expires_at)}</p>}
      <section className="z-flow">
        <h3>{I.replies} <span className="z-count">{number(data.items.length)}</span></h3>
        {data.items.map((reply) => reply.id === active?.id
          ? <div key={reply.id} className="z-review-item">
              <h4>{reply.target_author ? `@${reply.target_author}` : I.replyTarget}</h4>
              {reply.target_excerpt && <blockquote className="z-reply-quote z-agent-text">{reply.target_excerpt.text}</blockquote>}
              <p className="z-small"><a href={reply.target_url} target="_blank" rel="noreferrer noopener">{I.openOnX}</a></p>
              {reply.why && <p className="z-small z-muted">{reply.why}</p>}
              <textarea ref={editor} className="z-textarea z-agent-text" aria-label={I.replyDraft} rows={4} maxLength={1000} value={text} disabled={busy} onChange={(event) => setEdits({ ...edits, [reply.id]: event.target.value })} />
              <p className="z-small z-muted">{I.replyLength(length, X_LIMIT)}{length > X_LIMIT && <> · {I.replyTooLong}</>}</p>
              <p className="z-reply-actions">
                {link && <a className="z-btn soft" href={link} target="_blank" rel="noreferrer noopener">{I.replyOnX} <Kbd>O</Kbd></a>}
                <Button ref={primary} tone="primary" busy={busy} disabled={busy || !usable} onClick={() => void decide("posted")}>{I.markPosted} <Kbd>A</Kbd></Button>
                <Button tone="ghost" disabled={busy} onClick={() => void decide("skipped")}>{I.skipMessage} <Kbd>S</Kbd></Button>
              </p>
            </div>
          : <div key={reply.id} className="z-review-item z-reply-row">
              <Pill tone={reply.status === "queued" ? "teal" : "gray"} status>{I.replyStatus[reply.status] ?? reply.status}</Pill>
              <span className="z-small z-reply-summary">{reply.target_author ? `@${reply.target_author} · ` : ""}<span className="z-agent-text">{reply.body}</span></span>
              {reply.read_back === "found" && <span className="z-small z-muted z-reply-read">{reply.posted_url
                ? <a href={reply.posted_url} target="_blank" rel="noreferrer noopener">{reply.views === null ? I.replyFound : I.replyViews(number(reply.views))}</a>
                : I.replyFound}</span>}
              {reply.read_back === "missing" && <Chip variant="soft" tone="amber"><WarningCircleIcon size={12} />{I.replyMissing}</Chip>}
            </div>)}
      </section>
      {!data.x_connected && <p className="z-small z-muted">{I.repliesConnectX} <Link to="/p/$productId/data" params={{ productId: data.product_id }} search={{ view: "sources" }}>{I.repliesConnectXLink}</Link></p>}
      <ErrorText error={error} />
      <More label={I.rules} count={data.policies.length}>
        <ul className="z-points">{data.policies.map((policy) => <li key={policy.id}><span className="z-muted">{POLICY_KIND[policy.kind as keyof typeof POLICY_KIND] ?? policy.kind}</span> · {policy.rule}{policy.source && <> · <a href={policy.source} target="_blank" rel="noreferrer noopener">{policy.source}</a></>}</li>)}</ul>
      </More>
      <dl className="z-props">
        <dt>{I.tier}</dt><dd>{I.authorship[data.authorship] ?? data.authorship}</dd>
        {data.playbook && <><dt>{I.playbook}</dt><dd className="z-mono">{I.playbookVersion(data.playbook.id, data.playbook.version)}</dd></>}
        {data.acceptance.length > 0 && <><dt>{I.acceptance}</dt><dd><PointList items={data.acceptance} /></dd></>}
      </dl>
    </div>
  </Review>;
}
